import React, { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import * as pdfjsLib from "pdfjs-dist";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/search/lib/styles/index.css";
import "@react-pdf-viewer/thumbnail/lib/styles/index.css";
import "@react-pdf-viewer/zoom/lib/styles/index.css";
import "../../css/OutPutDirectory.css";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import {
  pdfDetails,
  pdfDownload,
  updatePdfField,
  savePdfData,
  resetPdfField
} from "../../redux/slices/PdfSlice";
import { ArrowBack } from "@mui/icons-material";
import { toast } from "react-toastify";
import { searchPlugin } from "@react-pdf-viewer/search";
import { thumbnailPlugin } from "@react-pdf-viewer/thumbnail";
import { zoomPlugin } from "@react-pdf-viewer/zoom";
import PdfThumbnailViewer from "./components/PdfThumbnailViewer";
import PdfViewer from "./components/PdfViewer";
import PdfDataPanel from "./components/PdfDataPanel";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const ActivityDetailsScreen = () => {
  const formatFieldName = (raw) => {
    if (raw === null || raw === undefined) return "";
    let s = String(raw);
    s = s.replace(/^p_+/, "");
    s = s.replace(/[_-]+$/, "");
    s = s.replace(/[_-]+/g, " ");
    s = s.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
    s = s.replace(/\s+/g, " ").trim();

    const acronyms = new Set(["id", "sku", "vat", "pan", "ifsc", "gst"]);
    return s
      .split(" ")
      .filter(Boolean)
      .map((w) => {
        const lower = w.toLowerCase();
        if (acronyms.has(lower)) return lower.toUpperCase();
        return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
      })
      .join(" ");
  };

  const PdfDataSlice = useSelector((state) => state.PdfDataSlice);
  const [editingCell, setEditingCell] = useState(null);
  const [editedValues, setEditedValues] = useState({});
  const [anchorEl, setAnchorEl] = React.useState(null);
  const openMenu = Boolean(anchorEl);
  const [tabIndex, setTabIndex] = useState(0);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [selectedPage, setSelectedPage] = useState(0);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [nestedTab, setNestedTab] = useState(null);
  const theme = useTheme();
  const { state } = useLocation();
  const { receviedDatafromProps } = state || {};
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const searchPluginInstance = searchPlugin();
  const thumbnailPluginInstance = thumbnailPlugin();
  const { Thumbnails } = thumbnailPluginInstance;
  const zoomPluginInstance = zoomPlugin();
  const { ZoomInButton, ZoomOutButton, ZoomPopover } = zoomPluginInstance;

  useEffect(() => {
    if (!state?.row?.id) return;
    dispatch(pdfDetails(state.row.id));
    dispatch(pdfDownload(state.row.id));
  }, [state, dispatch]);

  useEffect(() => {
    if (PdfDataSlice?.pdfSaveSuccess) {
      toast.success("PDF data saved successfully!");
      window.location.reload();
    } else if (PdfDataSlice?.pdfSaveError) {
      toast.error(`Failed to save PDF: ${PdfDataSlice?.pdfSaveError}`);
    }
  }, [PdfDataSlice?.pdfSaveSuccess, PdfDataSlice?.pdfSaveError]);

  const base64Data = PdfDataSlice?.pdfFile?.base64_data;
  const fileType = PdfDataSlice?.pdfFile?.mime_type;
  const pdfData = PdfDataSlice?.pdfData?.data?.data || [];
  const selectedPdfData =
    pdfData.length === 1 ? pdfData[0] : pdfData[selectedPage] || {};
  const isPdfFileLoading = PdfDataSlice?.pdfFileLoading;
  const isPdfDataLoading = PdfDataSlice?.pdfDataLoading;

  const createAndDownloadFile = (content, fileName, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const flattenObject = (obj, prefix = "") => {
    let result = {};
    Object.entries(obj).forEach(([key, value]) => {
      const newKey = prefix ? `${prefix} > ${key}` : key;
      if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value)
      ) {
        Object.assign(result, flattenObject(value, newKey));
      } else if (Array.isArray(value)) {
        value.forEach((item, index) => {
          Object.assign(result, flattenObject(item, `${newKey} [${index}]`));
        });
      } else {
        result[newKey] = value;
      }
    });
    return result;
  };

  const exportToCSV = () => {
    const updatedFileName = "Report";
    const flattenedData = pdfData.map((item) => flattenObject(item));
    const headers = Array.from(
      new Set(flattenedData.flatMap((obj) => Object.keys(obj)))
    );
    let csvContent = headers.join(",") + "\n";
    csvContent += flattenedData
      .map((item) =>
        headers.map((header) => `"${item[header] || ""}"`).join(",")
      )
      .join("\n");
    createAndDownloadFile(
      csvContent,
      `${updatedFileName}.csv`,
      "text/csv;charset=utf-8;"
    );
  };

  const exportToXML = () => {
    const updatedFileName = "Report";
    const convertToXML = (obj, indent = "  ") => {
      let xml = "";
      Object.entries(obj).forEach(([key, value]) => {
        if (
          typeof value === "object" &&
          value !== null &&
          !Array.isArray(value)
        ) {
          xml += `${indent}<${key}>\n${convertToXML(
            value,
            indent + "  "
          )}${indent}</${key}>\n`;
        } else if (Array.isArray(value)) {
          value.forEach((item) => {
            xml += `${indent}<${key}>\n${convertToXML(
              item,
              indent + "  "
            )}${indent}</${key}>\n`;
          });
        } else {
          xml += `${indent}<${key}>${value}</${key}>\n`;
        }
      });
      return xml;
    };
    let xmlContent = '<?xml version="1.0" encoding="UTF-8"?>\n<root>\n';
    pdfData.forEach((item) => {
      xmlContent += `  <item>\n${convertToXML(item, "    ")}  </item>\n`;
    });
    xmlContent += "</root>";
    createAndDownloadFile(
      xmlContent,
      `${updatedFileName}.xml`,
      "application/xml;charset=utf-8;"
    );
  };

  const exportToJSON = () => {
    const jsonString = JSON.stringify(pdfData, null, 2);
    createAndDownloadFile(
      jsonString,
      `Report.json`,
      "application/json;charset=utf-8;"
    );
  };

  const renderThumbnailItem = ({
    pageIndex,
    renderPageThumbnail,
    onJumpToPage,
  }) => (
    <div
      key={pageIndex}
      onClick={() => {
        setSelectedPage(pageIndex);
        onJumpToPage(pageIndex);
        setEditingCell(null);
      }}
      style={{
        cursor: "pointer",
        padding: "5px",
        border: selectedPage === pageIndex ? "2px solid blue" : "none",
      }}
    >
      {renderPageThumbnail}
    </div>
  );

  const handleSave = () => {
    dispatch(savePdfData({ id: state?.row?.id, pdfData: selectedPdfData }));
  };

  const handleView = (value) => {
    const stringValue = String(value).trim();
    const words = stringValue
      .replace(/\n/g, " ")
      .split(" ")
      .filter(Boolean);
    searchPluginInstance.clearHighlights();
    words.forEach((word) => {
      searchPluginInstance.highlight({
        keyword: word,
        matchCase: true,
        wholeWords: false,
      });
    });
  };

  const handleClearHighlights = () => {
    searchPluginInstance.clearHighlights();
  };

  const handleSaveEdit = (cellKey, field, lineIndex = null) => {
    const newValue = editedValues[cellKey];
    if (newValue === undefined) {
      setEditingCell(null);
      return;
    }
    let valueToStore = newValue;
    const trimmed = String(newValue).trim();
    if (
      (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
      (trimmed.startsWith("[") && trimmed.endsWith("]"))
    ) {
      try {
        valueToStore = JSON.parse(trimmed);
      } catch (e) {
        valueToStore = newValue;
      }
    }

    dispatch(
      updatePdfField({
        pageIndex: selectedPage,
        field,
        value: valueToStore,
        lineIndex,
      })
    );

    setEditingCell(null);
    handleClearHighlights();
  };

  const handleSaveNestedArrayEdit = (itemIndex, childKey, cellKey) => {
    if (!nestedTab) return;
    const newValue = editedValues[cellKey];
    if (newValue === undefined) {
      setEditingCell(null);
      return;
    }

    let parsed;
    try {
      parsed = JSON.parse(newValue);
    } catch (e) {
      parsed = newValue;
    }

    const currentArray = Array.isArray(nestedTab.data) ? nestedTab.data : [];
    const updatedArray = currentArray.map((item, idx) =>
      idx === itemIndex ? { ...item, [childKey]: parsed } : item
    );

    dispatch(
      updatePdfField({
        pageIndex: selectedPage,
        field: nestedTab.fieldKey,
        value: updatedArray,
        lineIndex: nestedTab.lineIndex ?? null,
      })
    );

    setNestedTab((prev) => (prev ? { ...prev, data: updatedArray } : prev));

    setEditingCell(null);
    handleClearHighlights();
  };

  const handleSaveNestedObjectEdit = (childKey, cellKey) => {
    if (!nestedTab) return;
    const newValue = editedValues[cellKey];
    if (newValue === undefined) {
      setEditingCell(null);
      return;
    }

    let parsed;
    try {
      parsed = JSON.parse(newValue);
    } catch (e) {
      parsed = newValue;
    }

    const currentObj =
      nestedTab.data && typeof nestedTab.data === "object"
        ? nestedTab.data
        : {};
    const updatedObj = { ...currentObj, [childKey]: parsed };

    dispatch(
      updatePdfField({
        pageIndex: selectedPage,
        field: nestedTab.fieldKey,
        value: updatedObj,
        lineIndex: nestedTab.lineIndex ?? null,
      })
    );

    setNestedTab((prev) => (prev ? { ...prev, data: updatedObj } : prev));

    setEditingCell(null);
    handleClearHighlights();
  };

  const handleResetNestedArrayEdit = (itemIndex, childKey, cellKey) => {
    if (!nestedTab || !PdfDataSlice?.pdfDataOriginal?.data?.data) return;
    const originalPages = PdfDataSlice.pdfDataOriginal.data.data;
    const originalPage = originalPages[selectedPage];
    if (!originalPage) return;
    let originalContainer;
    if (nestedTab.lineIndex !== null && nestedTab.lineIndex !== undefined) {
      const lineItems =
        originalPage["Line Items"] || originalPage["Items"];
      if (
        !Array.isArray(lineItems) ||
        !lineItems[nestedTab.lineIndex]
      ) {
        return;
      }
      originalContainer = lineItems[nestedTab.lineIndex][nestedTab.fieldKey];
    } else {
      originalContainer = originalPage[nestedTab.fieldKey];
    }

    const originalArray = Array.isArray(originalContainer)
      ? originalContainer
      : [];

    const resetValue =
      originalArray[itemIndex] && originalArray[itemIndex][childKey];

    if (typeof resetValue === "undefined") return;
    const currentArray = Array.isArray(nestedTab.data) ? nestedTab.data : [];
    const updatedArray = currentArray.map((item, idx) =>
      idx === itemIndex ? { ...item, [childKey]: resetValue } : item
    );
    dispatch(
      updatePdfField({
        pageIndex: selectedPage,
        field: nestedTab.fieldKey,
        value: updatedArray,
        lineIndex: nestedTab.lineIndex ?? null,
      })
    );
    setNestedTab((prev) => (prev ? { ...prev, data: updatedArray } : prev));
    setEditedValues((prev) => {
      const next = { ...prev };
      delete next[cellKey];
      return next;
    });

    setEditingCell(null);
    handleClearHighlights();
  };

  const handleResetNestedObjectEdit = (childKey, cellKey) => {
    if (!nestedTab || !PdfDataSlice?.pdfDataOriginal?.data?.data) return;

    const originalPages = PdfDataSlice.pdfDataOriginal.data.data;
    const originalPage = originalPages[selectedPage];
    if (!originalPage) return;

    let originalContainer;
    if (nestedTab.lineIndex !== null && nestedTab.lineIndex !== undefined) {
      const lineItems =
        originalPage["Line Items"] || originalPage["Items"];
      if (
        !Array.isArray(lineItems) ||
        !lineItems[nestedTab.lineIndex]
      ) {
        return;
      }
      originalContainer = lineItems[nestedTab.lineIndex][nestedTab.fieldKey];
    } else {
      originalContainer = originalPage[nestedTab.fieldKey];
    }

    const originalObj =
      originalContainer && typeof originalContainer === "object"
        ? originalContainer
        : {};

    const resetValue = originalObj[childKey];
    if (typeof resetValue === "undefined") return;

    const currentObj =
      nestedTab.data && typeof nestedTab.data === "object"
        ? nestedTab.data
        : {};

    const updatedObj = { ...currentObj, [childKey]: resetValue };
    dispatch(
      updatePdfField({
        pageIndex: selectedPage,
        field: nestedTab.fieldKey,
        value: updatedObj,
        lineIndex: nestedTab.lineIndex ?? null,
      })
    );
    setNestedTab((prev) => (prev ? { ...prev, data: updatedObj } : prev));
    setEditedValues((prev) => {
      const next = { ...prev };
      delete next[cellKey];
      return next;
    });
    setEditingCell(null);
    handleClearHighlights();
  };

  const handleReset = (cellKey, field, lineIndex = null) => {
    dispatch(
      resetPdfField({
        pageIndex: selectedPage,
        field,
        lineIndex,
      })
    );
    setEditedValues((prev) => {
      const next = { ...prev };
      delete next[cellKey];
      return next;
    });

    setEditingCell(null);
    handleClearHighlights();
  };

  const handleClickMenu = (event) => setAnchorEl(event.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);
  const handleSBClose = () => setShowSnackbar(false);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
    setEditingCell(null);
    setEditedValues({});
  };

  const handleFocus = (event) => {
    event.target.scrollIntoView({ block: "nearest", inline: "start" });
    const val = event.target.value;
    event.target.setSelectionRange(val.length, val.length);
  };

  const getRowCount = (value) => {
    const strValue = String(value);
    const lineBreaks = (strValue.match(/\n/g) || []).length;
    return Math.max(1, lineBreaks + 1);
  };

  const openNested = (fieldKey, data, lineIndex = null) => {
    const label = formatFieldName(fieldKey);
    const hasTables =
      !!selectedPdfData["Line Items"] || !!selectedPdfData["Items"];
    setNestedTab({ fieldKey, label, data, lineIndex });
    setTabIndex(hasTables ? 2 : 1);
  };

  const overlayActionsSx = (cellKey) => ({
    position: "absolute",
    top: 4,
    right: 4,
    display: "flex",
    gap: 0.8,
    alignItems: "center",
    transition: "opacity 0.12s ease",
    opacity:
      hoveredRow === cellKey && editingCell !== cellKey ? 1 : 0,
    pointerEvents:
      hoveredRow === cellKey && editingCell !== cellKey ? "auto" : "none",
    backgroundColor: "rgba(255,255,255,0.7)",
    borderRadius: "16px",
    padding: "2px 4px",
  });

  return (
    <div>
      {/* Header */}
      <div
        style={{
          marginLeft: 20,
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "row",
        }}
        className="nav-title"
      >
        <div style={{ flexDirection: "row", display: "flex" }}>
          <button
            onClick={() =>
              navigate(-1, {
                state: receviedDatafromProps,
              })
            }
            className="back-button"
          >
            <ArrowBack />
          </button>
          <h3>{"Documents"}</h3>
        </div>
      </div>

      {/* Main layout */}
      <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
        {/* 1. PDF Thumbnail viewer */}
        <PdfThumbnailViewer
          base64Data={base64Data}
          fileType={fileType}
          Thumbnails={Thumbnails}
          renderThumbnailItem={renderThumbnailItem}
        />

        {/* 2. PDF viewer */}
        <PdfViewer
          base64Data={base64Data}
          fileType={fileType}
          isPdfFileLoading={isPdfFileLoading}
          ZoomInButton={ZoomInButton}
          ZoomOutButton={ZoomOutButton}
          ZoomPopover={ZoomPopover}
          searchPluginInstance={searchPluginInstance}
          thumbnailPluginInstance={thumbnailPluginInstance}
          zoomPluginInstance={zoomPluginInstance}
        />

        {/* 3. PDF data panel */}
        <Box
          sx={{
            width: "40%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            overflow: "hidden",
            padding: "10px",
            position: "relative",
          }}
        >
          {isPdfDataLoading ? (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255,255,255,0.8)",
                zIndex: 10,
              }}
            >
              <CircularProgress color="inherit" />
            </Box>
          ) : (
            <PdfDataPanel
              selectedPdfData={selectedPdfData}
              pdfData={pdfData}
              theme={theme}
              tabIndex={tabIndex}
              handleTabChange={handleTabChange}
              editingCell={editingCell}
              setEditingCell={setEditingCell}
              editedValues={editedValues}
              setEditedValues={setEditedValues}
              nestedTab={nestedTab}
              setNestedTab={setNestedTab}
              hoveredRow={hoveredRow}
              setHoveredRow={setHoveredRow}
              handleSave={handleSave}
              handleView={handleView}
              handleSaveEdit={handleSaveEdit}
              handleSaveNestedArrayEdit={handleSaveNestedArrayEdit}
              handleSaveNestedObjectEdit={handleSaveNestedObjectEdit}
              handleResetNestedArrayEdit={handleResetNestedArrayEdit}
              handleResetNestedObjectEdit={handleResetNestedObjectEdit}
              handleReset={handleReset}
              handleFocus={handleFocus}
              getRowCount={getRowCount}
              openNested={openNested}
              overlayActionsSx={overlayActionsSx}
              exportToCSV={exportToCSV}
              exportToXML={exportToXML}
              exportToJSON={exportToJSON}
              anchorEl={anchorEl}
              openMenu={openMenu}
              handleClickMenu={handleClickMenu}
              handleCloseMenu={handleCloseMenu}
              showSnackbar={showSnackbar}
              setShowSnackbar={setShowSnackbar}
              handleSBClose={handleSBClose}
              formatFieldName={formatFieldName}
              hasTables={
                !!selectedPdfData["Line Items"] || !!selectedPdfData["Items"]
              }
            />
          )}
        </Box>
      </Box>
    </div>
  );
};

export default ActivityDetailsScreen;
